import { FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, Typography } from "@mui/material";
import { Mode } from "../../components/ReadWriteTextField";
import { ChangeEvent, useEffect, useState } from "react";
import { Loading } from "views/shared/Loading";
import { SectionHead } from "./SectionHead";
import { Estate } from "../../models/Estate";
import { useAuthContext } from "AuthContextWrapper"
import { NO_OPTION, YES_OPTION } from "components/ReadWriteOptionGroup";
import { useProbateContext } from "ProbateContextWrapper";
import { ProbateType } from "models/ProbateType";
import { ReadWriteWrapper } from "components/ReadWriteWrapper";
import { SelectState } from "components/SelectState";

interface IProps {
  allowedModes?: Mode[],
  startMode?: Mode,
}

interface AssetStats {
  numAssets:number, 
  totalPositiveValue:number, 
  totalNegativeValue:number,
  numUnvalued:number
}

export function EstateInfo(props:IProps):JSX.Element {
  const { getProbateDetails } = useProbateContext();
  const { activeEstate, setActiveEstate, states, user } = useAuthContext()
  const [mode, setMode] = useState<Mode>(props.startMode || "read")
  const [loadingFieldValues, setLoadingFieldValues] = useState<boolean>(true)
  const [error, setError] = useState<string>("")
  const [savedSuccessfully, setSavedSuccessfully] = useState<boolean | null>(null)
  const [estateAtEditStart, setEstateAtEditStart] = useState<Estate | undefined>()
  const [probateType, setProbateType] = useState<ProbateType|undefined>()
  const [assetStats, setAssetStats] = useState<AssetStats|undefined>()
  const [myEstate, setMyEstate] = useState<Estate>()
  
  useEffect(() => {
    if(!activeEstate) {
      return
    }
    const myEstate = Estate.fromJSON(activeEstate.toJson())
    myEstate.id = activeEstate.id
    setMyEstate(myEstate)
  }, [activeEstate?.id])

  useEffect(() => {
    async function getDetails() {
      if(probateType && probateType.id) {
        return
      }

      try {
        const details = await getProbateDetails()
        setProbateType(details?.probateType)
        console.log("got probate details")
      } catch(e) {
        setError("unable to fetch probate details")
      }
    }

    getDetails()
  }, [])

  useEffect(() => {
    if(!activeEstate?.assets) {
      return 
    }

    const summary:AssetStats = {
      numAssets: activeEstate.assets.length,
      totalPositiveValue: 0,
      totalNegativeValue: 0,
      numUnvalued: 0,
    }
    activeEstate.assets.map(asset => {
      if(!asset.value) {
        summary.numUnvalued += 1
      } else  if(asset.value > 0) {
        summary.totalPositiveValue += asset.value
      } else {
        summary.totalNegativeValue += asset.value
      }
    })

    setAssetStats(summary)
  }, [activeEstate?.assets])

  function canEdit(): boolean {
    if(!props.allowedModes) {
      return true
    }

    return !!props.allowedModes?.find(m => m === "edit")
  }

  async function save() {
    if(!myEstate) {
      console.warn("no estate yet")
      return
    }

    try {
      setError("")
      setSavedSuccessfully(null)

      // Save estate
      const savedEstate = await myEstate.saveEstate()
      if(!savedEstate?.id) {
        throw new Error("unknown error")
      }

      setActiveEstate(savedEstate)
      setMyEstate(savedEstate)
      setEstateAtEditStart(savedEstate)
      setSavedSuccessfully(true)
      setMode("read")
      setError("")
    } catch(e) {
      setError("unable to save estate info: " + (e as any)?.message)
    }
  }

  return <div>
    <SectionHead mode={mode} 
      canEdit={canEdit()} 
      header={"Estate Info"}  
      message={
        savedSuccessfully ? <Typography variant="body1" color="primary">Saved successfully</Typography> :
        error ? <Typography variant="body1" color="error">{error}</Typography> :
        <></>
      }
      onCancel={() => {
        setMode("read")
        setSavedSuccessfully(null)
        setMyEstate(estateAtEditStart)
      }}
      onEdit={() => {
        if(!myEstate) {
          setError("not yet done loading estate")
          return
        }
        const clone = Estate.fromJSON(myEstate.toJson())
        clone.id = myEstate.id
        setSavedSuccessfully(null)
        setEstateAtEditStart(clone)
        setMode("edit")
      }}
      onSave={() => save()}
    />
    
    <Grid container style={{marginBottom:"0.5rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Valid will
        </Typography>
        <ReadWriteWrapper
          value={myEstate?.hasWill}
          mode={mode} 
          blankReadOnlyValue="--"
        >
          <Select
            size ="small"
            readOnly={mode !== "edit"}
            onChange={(e:SelectChangeEvent) => {
              if(!myEstate) {
                return false
              }
  
              const newEstate = Estate.fromJSON(myEstate.toJson())
              newEstate.id = myEstate.id
              newEstate.hasWill = e.target.value == YES_OPTION.value ? "Yes" : "No"
              setMyEstate(newEstate)
              return true
            }}
            value={myEstate?.hasWill === "Yes" ? YES_OPTION.value.toString() : NO_OPTION.value.toString()}
          >
            {[YES_OPTION,NO_OPTION ].map((option,index) => {
                return <MenuItem 
                  key={"option" + index}
                  value={option.value.toString()} 
                >{option.label} </MenuItem> 
              })}
          </Select>
        </ReadWriteWrapper>
      </Grid>
    </Grid>

    <Grid container style={{marginBottom:"1rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Probate assignment
        </Typography>
        <Typography variant="body1" component="p">
          {probateType?.name || "--"}
        </Typography>
      </Grid>
    </Grid>

    <Grid container style={{marginBottom:"1rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Primary estate
        </Typography>
        <Typography variant="body1" component="div">
          <ReadWriteWrapper
            value={states?.find(s => s.id == myEstate?.stateId)?.name}
            mode={mode} 
            blankReadOnlyValue="--"
          >
          <SelectState 
            size="small"
            allowedCountryIds={myEstate?.countryId ? [myEstate.countryId] : undefined}
            stateId={myEstate?.stateId?.toString() || ""}
            onStateIdChange={(stateId:string) => {
              if(!myEstate) {
                return false
              }
  
              const newEstate = Estate.fromJSON(myEstate.toJson())
              newEstate.id = myEstate.id
              newEstate.stateId = parseInt(stateId || "0")
              setMyEstate(newEstate)
              return true
            }}
           
            />
            </ReadWriteWrapper>
        </Typography>
      </Grid>
    </Grid>
    
    <Grid container style={{marginBottom:"1.5rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Total estate assets
        </Typography>
        {assetStats ? <Typography variant="body1" component="p">
          <span style={{marginRight:"1rem"}}>
            ${assetStats.totalPositiveValue}
          </span> 
          {assetStats.numUnvalued > 0 && <span>( {assetStats.numUnvalued} unvalued )</span>}
        </Typography> : "--"}
      </Grid>
    </Grid>

    <Grid container style={{marginBottom:"1.5rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
      <Typography variant="body1" component="p" style={{fontWeight:"bold", marginBottom:"0.5rem"}}>
          Total estate debts, liens, and encumberances
        </Typography>
        {assetStats ? <Typography variant="body1" component="p">
          <span style={{marginRight:"1rem"}}>
            ${Math.abs(assetStats.totalNegativeValue)}
          </span> 
        </Typography> : "--"}
      </Grid>
    </Grid>
  </div>
}